import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { FloppyDisk, ArrowRight, QrCode } from "@phosphor-icons/react";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.profile?.name || user?.name || "",
    title: user?.profile?.title || "",
    company: user?.profile?.company || "",
    phone: user?.profile?.phone || "",
    website: user?.profile?.website || "",
    linkedin: user?.profile?.linkedin || "",
    tagline: user?.profile?.tagline || "",
    bio: user?.profile?.bio || "",
    calendly_url: user?.profile?.calendly_url || "",
    booking_cta: user?.profile?.booking_cta || "Book a meeting with me",
  });
  const [digestOptOut, setDigestOptOut] = useState(!!user?.digest_opt_out);
  const [digestBusy, setDigestBusy] = useState(false);

  const save = async () => {
    try { await api.put("/auth/profile", form); await refreshUser(); toast.success("Profile updated"); }
    catch { toast.error("Save failed"); }
  };

  const toggleDigest = async (next) => {
    setDigestBusy(true);
    try {
      await api.put("/auth/digest-preference", { opt_out: next });
      setDigestOptOut(next);
      await refreshUser();
      toast.success(next ? "Daily digest paused" : "Daily digest resumed");
    } catch { toast.error("Update failed"); }
    finally { setDigestBusy(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl" data-testid="profile-page">
      <Toaster theme="dark" />
      <div className="overline mb-2">// profile</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-2">Your operator card</h1>
      <p className="text-[#C7DDE5] mb-6">This information powers your digital business card and message templates.</p>

      {/* Quick link to My Card / QR code */}
      <Link
        to="/my-card"
        data-testid="profile-mycard-link"
        className="border p-4 mb-10 flex items-center justify-between gap-4 hover:bg-[#15303A] transition-colors"
        style={{ borderColor: "var(--accent)", background: "rgba(45,212,212,0.06)" }}
      >
        <div className="flex items-center gap-4 min-w-0">
          <QrCode size={32} weight="bold" color="var(--accent)" className="shrink-0" />
          <div className="min-w-0">
            <div className="overline" style={{ color: "var(--accent)" }}>// your scannable QR code lives here</div>
            <div className="font-display font-bold text-lg mt-1 truncate">Open My Card &rarr; scan to auto-save to any iPhone or Android</div>
          </div>
        </div>
        <ArrowRight weight="bold" className="shrink-0" />
      </Link>

      {/* Plan panel */}
      <div className="border p-6 mb-8" style={{ borderColor: "var(--border)" }} data-testid="subscription-panel">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="overline mb-2">// current plan</div>
            <div className="font-display font-bold text-2xl">Free · BETA</div>
            <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>You&apos;re on the free tier. Ready to scale? Book a call to scope Starter / Pro / Team pricing tailored to your business.</p>
          </div>
          <Link to="/book-a-call" data-testid="upgrade-btn" className="btn-primary flex items-center gap-2">Book a call <ArrowRight weight="bold" /></Link>
        </div>
      </div>

      {/* Booking integration */}
      <div className="border p-6 mb-8" style={{ borderColor: "var(--border)" }} data-testid="booking-panel">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <div className="overline mb-2">// booking link</div>
            <div className="font-display font-bold text-xl">Calendly + calendar sync</div>
            <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>Every contact who scans your card sees a &ldquo;Book a meeting&rdquo; button that opens this link.</p>
          </div>
          <Link to="/setup" data-testid="rerun-setup-btn" className="btn-secondary flex items-center gap-2">Re-run setup</Link>
        </div>
        <label className="label-base">Your Calendly URL</label>
        <input value={form.calendly_url || ""} onChange={(e) => setForm({ ...form, calendly_url: e.target.value })} className="input-base mb-4" placeholder="https://calendly.com/yourname/30min" data-testid="profile-calendly-url" />
        <label className="label-base">Button label</label>
        <input value={form.booking_cta || ""} onChange={(e) => setForm({ ...form, booking_cta: e.target.value })} className="input-base" placeholder="Book a meeting with me" data-testid="profile-booking-cta" />
      </div>

      {/* Daily digest */}
      <div className="border p-6 mb-8" style={{ borderColor: "var(--border)" }} data-testid="digest-panel">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="overline mb-2">// daily digest email</div>
            <div className="font-display font-bold text-xl">Overdue &amp; upcoming follow-ups</div>
            <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>
              Every morning at 8am ET you&apos;ll get a one-glance summary of what&apos;s overdue and what&apos;s coming up this week. Delivered via email.
            </p>
          </div>
          <button
            onClick={() => toggleDigest(!digestOptOut)}
            disabled={digestBusy}
            data-testid="digest-toggle-btn"
            className={digestOptOut ? "btn-secondary" : "btn-primary"}
          >
            {digestBusy ? "…" : digestOptOut ? "Resume digest" : "Pause digest"}
          </button>
        </div>
        <div className="mt-3 overline" style={{ color: digestOptOut ? "var(--text-2)" : "var(--accent)" }}>
          {digestOptOut ? "// paused — no emails" : "// active — receiving daily"}
        </div>
      </div>

      {/* Profile fields */}
      <div className="border p-6 grid grid-cols-1 md:grid-cols-2 gap-4" style={{ borderColor: "var(--border)" }}>
        {[
          ["name", "Name"], ["title", "Title"], ["company", "Company"], ["phone", "Phone"],
          ["website", "Website"], ["linkedin", "LinkedIn URL"], ["tagline", "Tagline (one-liner)"],
        ].map(([k, l]) => (
          <div key={k} className={k === "tagline" ? "md:col-span-2" : ""}>
            <label className="label-base">{l}</label>
            <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="input-base" data-testid={`profile-${k}`} />
          </div>
        ))}
        <div className="md:col-span-2">
          <label className="label-base">Bio</label>
          <textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="input-base"
            style={{ border: "1px solid var(--border)", padding: "12px" }} data-testid="profile-bio" />
        </div>
        <div className="md:col-span-2 mt-2">
          <button onClick={save} data-testid="save-profile-btn" className="btn-primary flex items-center gap-2"><FloppyDisk weight="bold" /> Save profile</button>
        </div>
      </div>
    </div>
  );
}
